import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import { chunk, uniqueId } from 'lodash';
import slugify from 'slugify';
import {
  Container,
  Text,
  GridItem,
  Button,
  Box,
  Flex,
  Center,
} from '@chakra-ui/react';
import { DeptGrid } from '@rsc/ui-components/dept/design-system';
import CardBlogStandard from '@rsc/ui-components/dept/atoms/CardBlogStandard';
import { useEffectOnce } from 'react-use';
import { getPageId } from '@rsc/utils/pagination';
import Pagination from '@rsc/ui-components/tailwind/Pagination';
import MiscNews from '@rsc/ui-components/dept/molecules/MiscNews';
import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

export default function GridBlogPaginated({
  data,
  location,
  firstBlockOnPage,
  siteSettings,
}) {
  const ref = useRef(null);
  const { category, posts } = data;
  const { tt } = useUiComponentsInternationalContext();

  const [chunks, setChunks] = useState([]);
  const [filter, setFilter] = useState({
    sub: null,
  });

  const customBlock = category?.blocks?.blocks?.find(
    b => b._type === 'deptGridBlogPaginated'
  );

  const pageId = getPageId(location);
  const pageElements = chunks[parseInt(pageId + 1, 10) - 1 || 0] || [];
  const searchParams = new URLSearchParams(location.search);

  // Clean up subcategories
  const subCategories = category.subcategories?.map(sub => sub.title) || [];

  // Formatting items subcategories to show only the ones that are in the subcategories array
  const items = posts.nodes.map(post => ({
    ...post,
    subCategories: Array.isArray(post.subCategories)
      ? post.subCategories.filter(sub => subCategories.includes(sub.title))
      : [],
  }));

  const getPressChunks = () => {
    const chunkSize = 11;

    const subCategoryParam = searchParams.get('sub');

    if (subCategoryParam) {
      const filteredItems = items.filter(item =>
        item.subCategories.some(
          sub => slugify(sub.title.toLowerCase()) === subCategoryParam
        )
      );

      return chunk(filteredItems, chunkSize);
    }

    return chunk(items, chunkSize);
  };

  useEffect(() => {
    const subCategoryParam = searchParams?.get('sub');

    setFilter({ ...filter, sub: subCategoryParam });
  }, [location]);

  useEffectOnce(() => {
    setChunks(getPressChunks());
  });

  useEffect(() => {
    const newChunks = getPressChunks();
    setChunks(newChunks);
  }, [filter]);
  //
  const handleParam = id => {
    if (!id) {
      searchParams.delete('sub');
    } else {
      searchParams.set('sub', id.toLowerCase());
      searchParams.delete('page');
    }

    const newSearch = `?${searchParams.toString()}`;
    if (newSearch !== location?.search) {
      navigate(newSearch);
    }
  };

  return (
    <Container
      ref={ref}
      {...generateSectionId({
        _key: uniqueId(),
        _type: 'deptGridBlogPaginated',
      })}
      pb={10}
    >
      <Box pt={10} pb={6}>
        <Text
          as={firstBlockOnPage ? 'h1' : 'h2'}
          textStyle="h2"
          color="brand.ink"
          mb={4}
        >
          {customBlock?.headline || category.title}
        </Text>
        <Text mb={10} maxW="46rem">
          {customBlock?.copy || category.description}
        </Text>
        {subCategories?.length > 0 && (
          <Flex
            gap={2}
            flexWrap="wrap"
            sx={{
              button: {
                fontSize: '0.875rem',
                fontWeight: 'normal',

                '&[data-active]': {
                  color: 'typography.lighter',
                  bg: 'brand.ink',
                  fontWeight: 'bold !important',
                },
              },
            }}
          >
            <Button
              isActive={!filter.sub}
              variant="tertiary"
              onClick={() => handleParam(null)}
              borderRadius="6.2rem"
            >
              {`${tt(s => s.blocksDeptComponents.allLabel)} ${category.title}`}
            </Button>

            {subCategories?.map(sub => {
              const id = slugify(sub, { strict: true, lower: true });
              return (
                <Button
                  key={id}
                  isActive={id === filter.sub}
                  variant="tertiary"
                  onClick={() => handleParam(id)}
                  borderRadius="6.2rem"
                >
                  {sub}
                </Button>
              );
            })}
          </Flex>
        )}
      </Box>

      <DeptGrid mb={10}>
        <GridItem colSpan={{ base: 6, sm: 6, md: 3, lg: 4 }} order={3}>
          <MiscNews h="full" newsletter={siteSettings.newsletter} />
        </GridItem>

        {pageElements.map((post, index) => (
          <GridItem
            key={post.slug.current}
            colSpan={{ base: 6, sm: 6, md: 3, lg: 4 }}
            order={index}
          >
            <CardBlogStandard article={post} />
          </GridItem>
        ))}
      </DeptGrid>
      <Center>
        <Pagination numOfPages={chunks.length} page={pageId} />
      </Center>
    </Container>
  );
}

GridBlogPaginated.defaultProps = {
  firstBlockOnPage: false,
};

GridBlogPaginated.propTypes = {
  firstBlockOnPage: PropTypes.bool,
  data: PropTypes.shape({
    posts: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          slug: PropTypes.shape({
            current: PropTypes.string.isRequired,
          }).isRequired,
        })
      ).isRequired,
    }).isRequired,
    category: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
        })
      ),
      blocks: PropTypes.shape({
        blocks: PropTypes.arrayOf(
          PropTypes.shape({
            _type: PropTypes.string.isRequired,
            headline: PropTypes.string.isRequired,
            copy: PropTypes.string.isRequired,
          })
        ),
      }),
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  siteSettings: PropTypes.shape({
    newsletter: PropTypes.shape({
      newsletterTitle: PropTypes.string,
      newsletterDescription: PropTypes.string,
    }),
  }).isRequired,
};
